import DOMPurify from 'dompurify';
import katex from 'katex';
import { createElement } from 'react';
import parse, { Element, HTMLReactParserOptions } from 'html-react-parser';

const replaceLatexExpressions = (content: string) => {
  const startToken = '{{latex:';
  let result = '';
  let currentIndex = 0;

  while (currentIndex < content.length) {
    const startIndex = content.indexOf(startToken, currentIndex);

    if (startIndex === -1) {
      result += content.slice(currentIndex);
      break;
    }

    result += content.slice(currentIndex, startIndex);

    let index = startIndex + startToken.length;
    let braceDepth = 0;

    while (index < content.length) {
      const char = content[index];

      if (char === '{') {
        braceDepth += 1;
      } else if (char === '}') {
        if (braceDepth > 0) {
          braceDepth -= 1;
        } else if (content[index + 1] === '}') {
          break;
        }
      }

      index += 1;
    }

    // Keep invalid/unclosed LaTeX unchanged.
    if (index >= content.length) {
      result += content.slice(startIndex);
      break;
    }

    const latex = content.slice(startIndex + startToken.length, index).trim();

    const escapedLatex = latex
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    result += `<span class="math-expression" data-latex="${escapedLatex}">${escapedLatex}</span>`;

    currentIndex = index + 2;
  }

  return result;
};

const safeParseHtml = (content: string, options?: HTMLReactParserOptions) => {
  if (!content) return null;

  const contentWithMath = content.includes('{{latex:')
    ? replaceLatexExpressions(content)
    : content;

  const cleanHtml = DOMPurify.sanitize(contentWithMath, {
    ALLOWED_TAGS: [
      'p',
      'br',
      'strong',
      'b',
      'em',
      'i',
      'u',
      'ol',
      'ul',
      'li',
      'span',
      'div',
      'sub',
      'sup',
      'fill-blank'
    ],
    ALLOWED_ATTR: ['class', 'data-latex']
  });

  const parserOptions: HTMLReactParserOptions = {
    ...options,

    replace: (node, index) => {
      if (node instanceof Element) {
        const className = node.attribs?.class;
        const latex = node.attribs?.['data-latex'];

        const isMathExpression = className
          ?.split(/\s+/)
          .includes('math-expression');

        if (isMathExpression && latex) {
          try {
            const renderedLatex = katex.renderToString(latex, {
              throwOnError: false,
              displayMode: false
            });

            return createElement('span', {
              key: index,
              className,
              'data-latex': latex,
              dangerouslySetInnerHTML: {
                __html: renderedLatex
              }
            });
          } catch (error) {
            console.error('KaTeX render error:', error);

            return createElement(
              'span',
              {
                key: index,
                className,
                'data-latex': latex
              },
              latex
            );
          }
        }
      }

      // Preserve existing custom parser behavior.
      if (options?.replace) {
        return options.replace(node, index);
      }

      return undefined;
    }
  };

  return parse(cleanHtml, parserOptions);
};

export default safeParseHtml;
